import {watch} from 'vue';
import {request} from '@/util/request';
import {fill} from "@/util/object";
import _ from "lodash";

export interface PageListConfig<T> {
    path?: string;
    pageNum?: number;
    pageSize?: number;
    condition?: any;
    load?: any;
    parser?: (origin: any[]) => Promise<T[]> | T[] | null;
    orderBy?: {
        column: string;
        order: 'asc' | 'desc';
    }[];
}

export interface PageListData<T> {
    loading: boolean;
    list: T[];
    count: number;
    path: string;
    pageNum: number;
    pageSize: number;
    condition: any;
    load?: any;
    orderBy: {
        column: string;
        order: 'asc' | 'desc';
    }[];
    parser?: (origin: any[]) => Promise<any[]>;
}

export class PageList<T = any> {
    public readonly data: PageListData<T>;

    private preParam: unknown = undefined;

    constructor(data: PageListConfig<T>) {
        this.data = fill(data as PageListData<T>, {
            list: [],
            count: 0,
            path: '',
            pageNum: 1,
            pageSize: 10,
            condition: {},
            orderBy: [],
            loading: false,
        })
    }

    async init() {
        watch(() => [this.data.condition, this.data.orderBy, this.data.pageSize, this.data.path],
            async () => {
                if (this.data.pageNum === 1) {
                    await this.fetchData();
                } else {
                    this.data.pageNum = 1;
                }
            },
            {deep: true});
        watch(
            () => this.data.pageNum,
            async () => {
                await this.fetchData();
            },
            {deep: true}
        );
        await this.fetchData();
    }

    async refresh() {
        this.preParam = undefined;
        return this.fetchData();
    }

    async replaceData(callback: (row: any) => any) {
        const parser = this.data.parser;
        const changeIndex: number[] = [];
        let changeData: any[] = [];
        this.data.list.forEach((item, index) => {
            const newData = callback(item)
            if (newData) {
                changeIndex.push(index)
                changeData.push(newData)
            }
        })
        if (parser) {
            changeData = await parser(changeData);
        }
        changeData.map((item, i) => {
            this.data.list[changeIndex[i]] = item;
        })
    }

    async fetchData() {
        this.data.loading = true;
        try {
            const param = {
                path: this.data.path,
                data: {
                    condition: this.data.condition,
                    orderBy: this.data.orderBy,
                    offset: this.data.pageSize * (this.data.pageNum - 1),
                    load: this.data.load,
                    limit: this.data.pageSize,
                }
            }
            if (!_.isEqual(this.preParam, param)) {
                this.preParam = _.cloneDeep(param);
                const res = await request(param.path, param.data);
                if (res) {
                    const parser = this.data.parser;
                    if (parser) {
                        this.data.list = await parser(res.list);
                    } else {
                        this.data.list = res.list;
                    }
                    this.data.count = res.count * 1;
                } else {
                    this.preParam = undefined;
                }
            }
        } finally {
            this.data.loading = false;
        }
    }
}
